import api from '../services/api';

/**
 * Shared photo loading utility for lazy-loading person photos
 * Used by CanvasPage and EditPersonModal
 *
 * Provides:
 * - Loading photos from BFF endpoint via filename
 * - Converting blob responses to data URLs
 * - Caching to avoid repeated API calls
 */

interface PhotoLoaderOptions {
  onError?: (error: Error) => void;
  useCache?: boolean;
}

// Singleton cache for photo data URLs
// Key: photoUrl (e.g., "/uploads/photos/guid.png")
// Value: data URL (e.g., "data:image/png;base64,...")
const photoCache: { [url: string]: string } = {};

/**
 * Load a photo from the BFF endpoint and convert to data URL
 *
 * @param photoUrl - The photo URL from the database (e.g., "/uploads/photos/guid.png")
 * @param options - Configuration options
 * @returns Promise<string> - Data URL for the image, or empty string on error
 *
 * Flow:
 * 1. Check cache for photoUrl
 * 2. Extract filename from photoUrl
 * 3. Request blob from BFF endpoint: GET /api/photos/{filename}
 * 4. Convert blob to data URL using FileReader
 * 5. Cache result and return
 */
export const loadPhotoAsDataUrl = async (
  photoUrl: string,
  options: PhotoLoaderOptions = {}
): Promise<string> => {
  const { useCache = true } = options;

  // Return cached version if available
  if (useCache && photoCache[photoUrl]) {
    return photoCache[photoUrl];
  }

  try {

    // Extract filename from photoUrl (e.g., "/uploads/photos/guid.jpg" -> "guid.jpg")
    const filename = photoUrl.split('/').pop() || '';
    if (!filename) {
      options.onError?.(new Error('Invalid photo URL format'));
      return '';
    }


    // Request photo from the configured BFF API base URL.
    const response = await api.get(`/photos/${filename}`, {
      responseType: 'blob',
    });


    // Convert blob to data URL
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        // Cache it
        photoCache[photoUrl] = dataUrl;
        resolve(dataUrl);
      };
      reader.onerror = () => {
        const error = new Error('FileReader failed to read photo blob');
        options.onError?.(error);
        reject(error);
      };
      reader.readAsDataURL(response.data);
    });
  } catch (error: any) {
    options.onError?.(error);
    // Return empty string on error - will fall back to default avatar
    return '';
  }
};

/**
 * Clear photo cache
 *
 * @param photoUrl - Optional: clear only this specific photo. If not provided, clears all.
 */
export const clearPhotoCache = (photoUrl?: string): void => {
  if (photoUrl) {
    delete photoCache[photoUrl];
  } else {
    Object.keys(photoCache).forEach(key => delete photoCache[key]);
  }
};

/**
 * Get cache statistics (useful for debugging)
 */
export const getPhotoCacheStats = (): { size: number; keys: string[] } => {
  return {
    size: Object.keys(photoCache).length,
    keys: Object.keys(photoCache),
  };
};
